import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
    BookOpen, Search, ChevronDown, ChevronRight,
    LogIn, LayoutGrid, ShoppingBasket, ChefHat, Receipt, Calendar,
    UtensilsCrossed, QrCode, Sparkles, Plug, CreditCard, Users, Smartphone,
    AlertCircle, Lightbulb, Printer, Settings, BarChart3, Download, FileText, ScrollText, Power,
} from "lucide-react";

// ===== Sezioni della guida =====
const SECTIONS = [
    {
        id: "quickstart",
        title: "Avvio rapido",
        icon: LogIn,
        roles: ["admin", "cashier", "waiter", "kitchen"],
        items: [
            {
                id: "first-login",
                title: "Primo accesso (Admin)",
                body: (
                    <>
                        <p className="mb-3">Smart Table 360 supporta tre modalità di accesso:</p>
                        <ol className="list-decimal pl-5 space-y-2 text-sm">
                            <li><strong>Google Admin</strong> — clicca "Accedi come Admin con Google" sulla pagina <code>/login</code>. Il primo utente Google diventa automaticamente admin.</li>
                            <li><strong>Email + Password</strong> — credenziali del terminale (es. <code>cassa@miristorante.it</code>).</li>
                            <li><strong>QR Cameriere</strong> — accesso istantaneo da fotocamera del telefono (vedi sezione "QR Camerieri").</li>
                        </ol>
                        <div className="mt-4 bg-amber-900/20 border border-amber-700/30 rounded-md p-3 text-amber-200 text-xs">
                            <AlertCircle className="w-4 h-4 inline mr-1" /> All'attivazione hai 14 giorni di trial Premium gratuiti. Dopo la scadenza serve un abbonamento attivo dalla pagina <strong>Abbonamento</strong>.
                        </div>
                    </>
                ),
            },
            {
                id: "create-staff",
                title: "Crea i terminali del personale",
                body: (
                    <>
                        <p className="mb-3">Vai su <strong>Sidebar → Utenti</strong> e crea gli account per ogni ruolo:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li><strong>Cameriere</strong> — vede Mappa Sala, Nuovo Ordine, Menu (sola lettura), Prenotazioni</li>
                            <li><strong>Cucina</strong> — vede solo il Terminale Cucina (KDS)</li>
                            <li><strong>Cassa</strong> — Dashboard, Cassa, Scontrini, Report</li>
                            <li><strong>Admin</strong> — accesso completo + Impostazioni, Stampanti, Integrazione Prenova, Abbonamento</li>
                        </ul>
                        <p className="mt-3 text-sm">Per ogni cameriere, dalla pagina <strong>Terminali Camerieri</strong> puoi visualizzare il QR personale e stamparlo.</p>
                    </>
                ),
            },
            {
                id: "configure-room",
                title: "Configura sala e tavoli",
                body: (
                    <>
                        <p className="mb-3">Vai su <strong>Mappa Sala</strong> → tasto <strong>Modifica Layout</strong>:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Crea le sale (es. "Interno", "Dehor", "Privé")</li>
                            <li>Trascina i tavoli sulla mappa, imposta numero, posti, forma</li>
                            <li>Genera il <strong>QR del tavolo</strong> da mettere sui tavoli (Impostazioni → QR per tavolo)</li>
                            <li>Stampa A4 con 2 QR per pagina dal tasto "Stampa tutti"</li>
                        </ul>
                    </>
                ),
            },
        ],
    },

    {
        id: "table-flow",
        title: "Flusso operativo: dal tavolo al pagamento",
        icon: LayoutGrid,
        roles: ["admin", "cashier", "waiter"],
        items: [
            {
                id: "open-table",
                title: "1. Apertura tavolo",
                body: (
                    <ol className="list-decimal pl-5 space-y-2 text-sm">
                        <li>Sulla <strong>Mappa Sala</strong> tocca un tavolo libero (verde) → si apre il pannello dettaglio</li>
                        <li>Tocca <strong>"Apri ordine"</strong> → il tavolo passa a <strong>Occupato</strong> (giallo) e ti porta su <code>/ordine/{`{tavolo}`}</code></li>
                        <li>Inserisci numero coperti se richiesto dal menu attivo (per il calcolo automatico del coperto)</li>
                    </ol>
                ),
            },
            {
                id: "add-items",
                title: "2. Inserire la comanda",
                body: (
                    <>
                        <p className="mb-2 text-sm">Su <strong>Nuovo Ordine</strong> (o <code>/ordine/{`{tavolo}`}</code>):</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Filtra per categoria, cerca per nome, tocca un piatto per aggiungerlo</li>
                            <li>Inserisci <strong>note</strong> (es. "senza glutine", "ben cotto")</li>
                            <li>Cambia <strong>quantità</strong> con i pulsanti +/-</li>
                            <li>Imposta la <strong>portata</strong> (antipasto, primo, secondo, contorno, dolce) — la cucina vede l'ordine raggruppato per portata</li>
                            <li>Tocca <strong>"Invia in cucina"</strong> → comanda inviata al KDS in tempo reale via WebSocket</li>
                        </ul>
                    </>
                ),
            },
            {
                id: "kitchen-flow",
                title: "3. Cucina: preparazione e ritiro",
                body: (
                    <>
                        <p className="mb-2 text-sm">Sul <strong>Terminale Cucina (KDS)</strong>:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Comande nuove appaiono in alto con <strong>animazione</strong> + suono (futuro)</li>
                            <li>Sfondo <strong>giallo</strong> se attesa ≥20 min, <strong>rosso pulsante</strong> se ≥30 min</li>
                            <li>Tocca <strong>"In preparazione"</strong> → il piatto passa allo stato giallo</li>
                            <li>Tocca <strong>"Pronto"</strong> → il cameriere riceve notifica push WebSocket</li>
                            <li>Tocca <strong>"Ritirato"</strong> quando il piatto esce dalla cucina → diventa <strong>"Consegnato"</strong></li>
                        </ul>
                    </>
                ),
            },
            {
                id: "request-bill",
                title: "4. Richiesta del conto",
                body: (
                    <ol className="list-decimal pl-5 space-y-2 text-sm">
                        <li>Sul terminale cameriere, dal tavolo aperto, tocca <strong>"Conto"</strong></li>
                        <li>Il tavolo cambia stato e la cassa riceve la notifica</li>
                        <li>Il cliente può anche richiedere il conto direttamente dal portale <code>/c</code> (se ha scansionato il QR del tavolo)</li>
                    </ol>
                ),
            },
            {
                id: "payment",
                title: "5. Cassa e pagamento",
                body: (
                    <>
                        <p className="mb-2 text-sm">Su <strong>Terminale Cassa → tavolo specifico</strong> (<code>/cassa/{`{tavolo}`}</code>):</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li><strong>Sconto globale</strong>: % o netto € sul totale</li>
                            <li><strong>Sconto per piatto</strong>: bottone "Sconto piatto" su ogni riga</li>
                            <li><strong>Articolo libero</strong>: pulsante per aggiungere extra (es. caffè) con prezzo e IVA personalizzati</li>
                            <li><strong>Modalità conto</strong>:
                                <ul className="list-disc pl-6 mt-1">
                                    <li><em>Unico</em>: paga tutto in una volta</li>
                                    <li><em>Romana</em>: divide il totale per N persone</li>
                                    <li><em>Dividi per piatto</em>: seleziona quali piatti pagare ora (per gruppi)</li>
                                </ul>
                            </li>
                            <li><strong>Metodo</strong>: Contanti / Carta / Bancomat / Satispay / Bonifico / Ticket / Misto</li>
                            <li><strong>Documento</strong>: Scontrino o Fattura (con dati cliente: CF, P.IVA, SDI/PEC, indirizzo)</li>
                            <li>Tocca <strong>"Conferma pagamento"</strong> → emesso documento con numero progressivo, tavolo torna libero</li>
                        </ul>
                        <div className="mt-3 bg-brand/10 border border-brand/30 rounded-md p-3 text-xs text-warm">
                            <Lightbulb className="w-4 h-4 inline mr-1 text-brand" /> <strong>Calcolatrice integrata</strong>: in cassa puoi calcolare il resto da € 10/20/50/100 con un solo tap.
                        </div>
                    </>
                ),
            },
            {
                id: "quick-sale",
                title: "Vendita al banco (no tavolo)",
                body: (
                    <>
                        <p className="text-sm mb-2">Per caffè al banco, vendite veloci o asporto:</p>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                            <li>Vai su <strong>Cassa</strong> → tocca <strong>"Conto Libero / Banco"</strong></li>
                            <li>Tab <em>Prodotti Menu</em>: scegli dal catalogo</li>
                            <li>Tab <em>Articolo libero</em>: inserisci nome+prezzo manualmente</li>
                            <li>Imposta sconto, metodo e documento → emetti</li>
                        </ol>
                    </>
                ),
            },
        ],
    },

    {
        id: "menus",
        title: "Multi-menu (Pranzo / Cena / Eventi)",
        icon: UtensilsCrossed,
        roles: ["admin"],
        items: [
            {
                id: "create-menu",
                title: "Creare un menu separato",
                body: (
                    <>
                        <p className="text-sm mb-2">Da <strong>Sidebar → Gestione Menu</strong>:</p>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                            <li>Tocca <strong>"Nuovo Menu"</strong> → nome (es. "Cena"), descrizione</li>
                            <li>Imposta <strong>Coperto</strong>: abilita/disabilita + importo €</li>
                            <li>Tocca <strong>Attiva</strong> per renderlo il menu corrente del servizio</li>
                            <li>Solo UN menu è attivo alla volta. Cassa, Cameriere e Cucina lo vedono in tempo reale.</li>
                        </ol>
                    </>
                ),
            },
            {
                id: "duplicate-menu",
                title: "Duplicare un menu esistente",
                body: (
                    <p className="text-sm">
                        Click sull'icona <strong>Copy</strong> di una card menu → assegna un nuovo nome → tutte le categorie e i piatti vengono clonati nel nuovo menu (utile per partire da un menu base e modificarlo per gli eventi).
                    </p>
                ),
            },
            {
                id: "service-prompt",
                title: "Prompt apertura servizio",
                body: (
                    <p className="text-sm">
                        All'apertura del Dashboard la prima volta del giorno, se hai ≥2 menu, appare automaticamente il prompt <em>"Quale menu attiviamo oggi?"</em>. Clicca quello giusto e parti subito. La scelta è confermata anche per gli altri terminali via WebSocket.
                    </p>
                ),
            },
        ],
    },

    {
        id: "novita",
        title: "Novità 2026 — Menu del Giorno, Database Piatti, Auto-Deliver",
        icon: ScrollText,
        roles: ["admin"],
        items: [
            {
                id: "menu-del-giorno",
                title: "Menu del Giorno — Builder con Database Piatti",
                body: (
                    <>
                        <p className="text-sm mb-2">Da <strong>Sidebar → Menu del Giorno</strong> (admin) costruisci menu giornalieri pescando dal Database Piatti centrale (Master DB).</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li><strong>Database Piatti</strong>: magazzino centrale di TUTTI i piatti — search, filtro per portata, badge ⚠ se mancano gli allergeni.</li>
                            <li><strong>Builder</strong>: aggiungi piatti dal Master DB (con filtro tipologia) o creane di nuovi (opzione "Salva anche nel Database").</li>
                            <li><strong>Anteprima/Stampa/Download PDF</strong> elegante per ogni menu, con logo, allergeni e coperto.</li>
                            <li>Le categorie nel PDF seguono sempre l'ordine canonico: <em>Antipasti → Primi → Secondi → Contorni → Dolci → Bevande</em>.</li>
                        </ul>
                    </>
                ),
            },
            {
                id: "attiva-menu",
                title: "Bottone ATTIVA MENU — riusa il menu ogni settimana",
                body: (
                    <>
                        <p className="text-sm mb-2">Una volta creato un Menu del Giorno, clicca <strong className="text-brand">ATTIVA MENU</strong> sulla sua card:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Il menu viene caricato come permanente in <strong>Gestione Menu</strong> e impostato come ATTIVO.</li>
                            <li>Camerieri, clienti e cucina lo vedono immediatamente sui terminali (via WebSocket).</li>
                            <li><strong>Stesso nome</strong> = stesso menu permanente. Crea "Lunedì" e ri-attivalo ogni lunedì: i piatti vengono ricaricati.</li>
                            <li>Badge <em>✓ Attivo sui terminali</em> mostra quale menu del giorno è attualmente live.</li>
                            <li>Da Gestione Menu puoi attivare/disattivare con un click qualsiasi menu salvato.</li>
                        </ul>
                    </>
                ),
            },
            {
                id: "terminato-live",
                title: "Badge TERMINATO live + audio sui terminali",
                body: (
                    <>
                        <p className="text-sm mb-2">Quando un piatto finisce:</p>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                            <li>Apri <strong>Menu del Giorno → Database Piatti</strong></li>
                            <li>Clicca il bottone rosso <strong>TERMINA</strong> a destra del piatto</li>
                            <li>Tutti i terminali staff sentono un suono e vedono toast <em>🍽️ TERMINATO — Nome piatto</em></li>
                            <li>Cameriere e cliente vedono il badge rosso <strong>TERMINATO</strong> sul piatto, non cliccabile</li>
                            <li>Clicca <strong>Riattiva</strong> quando torna disponibile</li>
                        </ol>
                        <p className="text-sm mt-3 text-elegant">Il bottone "SUONI" in alto a destra controlla il muto globale.</p>
                    </>
                ),
            },
            {
                id: "auto-deliver",
                title: "Sequenza automatica print/mixed — bevande 5 min, piatti 12 min",
                body: (
                    <>
                        <p className="text-sm mb-2">Quando una stazione (Cucina/Bar/Pizza) è in modalità <strong>Stampante</strong> o <strong>Mista</strong>:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>All'invio della comanda → status <code>in_preparazione</code> automatico (salta "Accetta")</li>
                            <li><strong>Bevande</strong> (course=bevanda o destinazione=bar) → <strong>consegnato automatico dopo 5 minuti</strong></li>
                            <li><strong>Piatti</strong> (kitchen/pizza) → <strong>consegnato automatico dopo 12 minuti</strong></li>
                            <li>Quando tutti gli item sono consegnati → ordine archiviato automaticamente</li>
                            <li>In modalità <strong>Solo Video</strong> resta il flusso manuale KDS (Accetta → Pronto → Portato)</li>
                        </ul>
                        <p className="text-sm mt-3 text-elegant">Cambia modalità da <strong>Stampanti → Stazioni</strong>: Video / Print / Mixed per ogni reparto.</p>
                    </>
                ),
            },
            {
                id: "logo-brand",
                title: "Logo & Brand — identità sui PDF",
                body: (
                    <p className="text-sm">
                        Da <strong>Menu del Giorno → Logo & Brand</strong> carica il logo del ristorante (PNG/JPG/WebP/SVG ≤ 2 MB), imposta il nome e i colori del PDF. Il logo apparirà in alto su ogni menu stampato. <strong>Senza indirizzo, senza foto piatti</strong> — solo identità essenziale.
                    </p>
                ),
            },
            {
                id: "gestione-sale",
                title: "Gestione sale — rinomina ed elimina",
                body: (
                    <>
                        <p className="text-sm mb-2">In <strong>Mappa Sala</strong>, sulla sala ATTIVA (chip selezionato) trovi due icone:</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>✎ <strong>Rinomina</strong> → cambia nome senza perdere il layout dei tavoli</li>
                            <li>🗑 <strong>Elimina</strong> → se i tavoli sono tutti liberi, elimina sala + tavoli; altrimenti blocca l'azione</li>
                        </ul>
                    </>
                ),
            },
            {
                id: "ppt-training",
                title: "PowerPoint formazione scaricabile",
                body: (
                    <p className="text-sm">
                        In alto a destra in questa pagina trovi il bottone <strong className="text-brand">Scarica PPT Formazione</strong>. Genera un file <code>.pptx</code> con 16 slide e screenshot completi per formare nuovi camerieri/cucina/cassa. Pronto da proiettare. Include cover personalizzata, esempio di serata, best practices e troubleshooting.
                    </p>
                ),
            },
        ],
    },

    {
        id: "stampanti-lan",
        title: "Stampanti — LAN, USB, port forward",
        icon: Printer,
        roles: ["admin"],
        items: [
            {
                id: "lan-cloud-limit",
                title: "Perché la stampante LAN (es. 192.168.x.x) non funziona dal cloud",
                body: (
                    <>
                        <p className="text-sm mb-2">Smart Table gira su un server cloud Emergent. <strong>Gli IP privati come <code>192.168.0.42</code>, <code>10.0.0.x</code>, <code>172.16.x.x</code></strong> sono nella tua rete locale e <strong>NON sono raggiungibili dal cloud</strong>. È un limite della rete TCP/IP, non un bug dell'app.</p>
                        <p className="text-sm text-elegant">Quando provi "Test stampa" verso un IP privato, il sistema te lo segnala subito con un messaggio che ti spiega le alternative.</p>
                    </>
                ),
            },
            {
                id: "lan-solution-usb",
                title: "Soluzione consigliata: USB diretta dal browser (WebUSB)",
                body: (
                    <ol className="list-decimal pl-5 space-y-1 text-sm">
                        <li>Collega la stampante (es. Munbyn) via cavo <strong>USB</strong> al PC/tablet dove apri Smart Table.</li>
                        <li>Vai in <strong>Stampanti → Aggiungi stampante → USB</strong>.</li>
                        <li>Il browser mostra il dialog di selezione → scegli la Munbyn.</li>
                        <li>Test stampa subito. Funziona finché il PC è acceso e ha Smart Table aperto.</li>
                        <li><strong>Pro:</strong> zero rete, zero firewall, zero rischi sicurezza. <strong>Contro:</strong> 1 stampante = 1 postazione fissa.</li>
                    </ol>
                ),
            },
            {
                id: "lan-solution-portforward",
                title: "Soluzione 2: Port forwarding sul router",
                body: (
                    <>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                            <li>Apri la configurazione del tuo router (di solito <code>192.168.1.1</code> o <code>192.168.0.1</code>).</li>
                            <li>Crea una regola <strong>Port Forwarding</strong>: porta esterna <code>9100</code> → IP interno <code>192.168.0.42</code> porta <code>9100</code>.</li>
                            <li>Riserva un <strong>DHCP statico</strong> per la Munbyn così non cambia IP.</li>
                            <li>Trova il tuo <strong>IP pubblico</strong> (whatismyip.com).</li>
                            <li>In Smart Table → Stampanti → modifica → IP = il tuo IP pubblico (NON 192.168.x.x).</li>
                        </ol>
                        <p className="text-sm mt-3 text-amber-300"><strong>⚠ Attenzione sicurezza:</strong> chiunque su internet potrebbe inviare stampe alla tua stampante. Restringi con firewall solo agli IP del cloud Emergent o usa una VPN.</p>
                    </>
                ),
            },
            {
                id: "lan-print-test",
                title: "Diagnostica veloce",
                body: (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li><strong>Errore "IP privato non raggiungibile"</strong> → vedi soluzioni sopra.</li>
                        <li><strong>Timeout 6 secondi</strong> → stampante spenta, cavo scollegato, IP/porta sbagliati, oppure firewall del router blocca la porta.</li>
                        <li><strong>Connessione rifiutata</strong> → IP raggiungibile ma porta 9100 chiusa. Verifica config ESC/POS della stampante.</li>
                        <li><strong>USB funziona ma LAN no</strong> → la stampante c'è ma il problema è di rete. Usa USB finché non sistemi il port forward.</li>
                    </ul>
                ),
            },
        ],
    },

    {
        id: "ai-import",
        title: "Import AI del menu (PDF/foto)",
        icon: Sparkles,
        roles: ["admin"],
        items: [
            {
                id: "ai-import-flow",
                title: "Importare un menu cartaceo o PDF",
                body: (
                    <>
                        <ol className="list-decimal pl-5 space-y-2 text-sm">
                            <li>Vai su <strong>Gestione Menu</strong> → scegli il menu di destinazione</li>
                            <li>Click sull'icona <Sparkles className="w-3.5 h-3.5 inline text-brand" /> verde sulla card</li>
                            <li>Trascina o seleziona un file <strong>PDF, JPG, PNG o WebP</strong> (max 15 MB)</li>
                            <li>Click <strong>"Analizza con AI"</strong> → l'AI Vision di Gemini estrae categorie, piatti, prezzi (30-60 sec)</li>
                            <li>Rivedi l'<strong>anteprima editabile</strong>: modifica nomi/prezzi, rimuovi voci sbagliate</li>
                            <li>Scegli modalità: <em>Aggiungi al menu esistente</em> oppure <em>Sostituisci tutto</em></li>
                            <li>Click <strong>"Salva N piatti"</strong> → categorie e piatti vengono creati</li>
                        </ol>
                        <div className="mt-3 bg-amber-900/20 border border-amber-700/30 rounded-md p-3 text-amber-200 text-xs">
                            <AlertCircle className="w-4 h-4 inline mr-1" /> Ogni analisi consuma <strong>1 unità di quota AI</strong>. Hai 10 unità gratuite al mese; pacchetti da 50 a €10 dalla pagina Abbonamento.
                        </div>
                        <div className="mt-2 bg-brand/10 border border-brand/30 rounded-md p-3 text-xs text-warm">
                            <Lightbulb className="w-4 h-4 inline mr-1 text-brand" /> <strong>Best practice</strong>: scatta foto frontali, ben illuminate, senza riflessi. Per menu lunghi (più pagine) PDF è preferibile.
                        </div>
                    </>
                ),
            },
        ],
    },

    {
        id: "qr-waiters",
        title: "QR personali per camerieri",
        icon: QrCode,
        roles: ["admin"],
        items: [
            {
                id: "qr-show",
                title: "Mostrare i QR sulla pagina Terminali",
                body: (
                    <p className="text-sm">
                        Vai su <strong>Sidebar → Terminali Camerieri</strong>. Ogni cameriere attivo ha la propria card con QR visibile. Il cameriere apre la fotocamera del telefono, inquadra il QR e accede istantaneamente al proprio terminale (senza inserire password). Tocca <strong>"Ingrandisci"</strong> sulla card per visualizzarlo più grande.
                    </p>
                ),
            },
            {
                id: "qr-print",
                title: "Stampare i QR",
                body: (
                    <p className="text-sm">
                        Click <strong>"Stampa tutti i QR"</strong> in alto a destra → si apre una pagina A4 pronta per la stampa con 2 QR per foglio. Ritaglia e consegna a ogni cameriere (o tieni il foglio in cassa come backup).
                    </p>
                ),
            },
            {
                id: "qr-rotate",
                title: "Rigenerare un QR (sicurezza)",
                body: (
                    <p className="text-sm">
                        Se un cameriere perde il foglio o se ne va, click sull'icona <strong>↻</strong> sulla sua card → conferma → il vecchio QR viene <strong>invalidato</strong> e ne viene generato uno nuovo. Stampa il nuovo e distruggi il vecchio.
                    </p>
                ),
            },
        ],
    },

    {
        id: "reservations",
        title: "Prenotazioni & integrazione renova360",
        icon: Calendar,
        roles: ["admin", "cashier", "waiter"],
        items: [
            {
                id: "reservations-flow",
                title: "Gestione prenotazioni",
                body: (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Sidebar → <strong>Prenotazioni</strong>: calendario giornaliero/settimanale con orario, nome, persone, allergie, animale</li>
                        <li>Bottone <strong>"+ Nuova"</strong> per aggiungere prenotazione manuale</li>
                        <li>Bottone <strong>"Conferma"</strong> rosso pulsante per le prenotazioni Prenova in stato <em>"Da confermare"</em>: notifica al cliente via email/SMS gestiti da Prenova</li>
                        <li>Bottone <strong>"Arrivato"</strong> → assegna tavolo e segna l'arrivo</li>
                        <li>Card Dashboard "Prenotazioni next 2h" mostra le prenotazioni imminenti con badge NEW per webhook in tempo reale</li>
                    </ul>
                ),
            },
            {
                id: "prenova-setup",
                title: "Connettere renova360",
                body: (
                    <>
                        <p className="text-sm mb-2"><strong>Sidebar → Collega renova360</strong>:</p>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                            <li>Inserisci <strong>API URL base</strong>, <strong>API Key</strong>, <strong>Restaurant ID</strong></li>
                            <li>Inserisci <strong>Push Path</strong> e <strong>Pull Path</strong> (URL completi forniti da Prenova)</li>
                            <li>Click <strong>"Test connessione"</strong> → verifica risposta dell'endpoint</li>
                            <li>Abilita <strong>Auto-sync</strong> per polling automatico ogni 60 sec</li>
                            <li>Configura il <strong>webhook secret</strong> in Prenova per ricevere eventi in tempo reale</li>
                        </ol>
                        <p className="text-sm mt-2">La sezione <strong>Log Sincronizzazione</strong> mostra gli ultimi 50 log con metodo, path, esito, timestamp e payload completo.</p>
                    </>
                ),
            },
        ],
    },

    {
        id: "insights",
        title: "Insights AI Sala",
        icon: BarChart3,
        roles: ["admin", "cashier"],
        items: [
            {
                id: "generate-insights",
                title: "Generare un report AI",
                body: (
                    <>
                        <p className="text-sm mb-2">
                            <strong>Sidebar → Insights AI Sala</strong>: click <strong>"Genera Insights"</strong> per ottenere un report Markdown italiano con 4 sezioni:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>📊 <strong>Sintesi del servizio</strong></li>
                            <li>🍽️ <strong>Top performer & best seller</strong></li>
                            <li>⏱️ <strong>Bottleneck cucina</strong> (tempi anomali)</li>
                            <li>🚀 <strong>Azioni consigliate</strong> (pricing, menu, staffing, marketing)</li>
                        </ul>
                        <p className="text-sm mt-2">Lo storico è salvato e consultabile. Ogni generazione consuma <strong>1 quota AI</strong>.</p>
                    </>
                ),
            },
        ],
    },

    {
        id: "closing",
        title: "Chiusura giornata",
        icon: Receipt,
        roles: ["admin", "cashier"],
        items: [
            {
                id: "closure",
                title: "Chiusura corrispettivi",
                body: (
                    <>
                        <p className="text-sm mb-2">
                            A fine servizio: <strong>Sidebar → Cassa → "Chiusura corrispettivi"</strong>
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Anteprima riepilogo: totale, IVA scorporata, count documenti, by metodo</li>
                            <li>Conferma → genera <strong>Z-number</strong> incrementale + <code>transmission_id</code> per Agenzia Entrate</li>
                            <li>Stampa scontrino chiusura formato 80mm</li>
                            <li>409 se hai già chiuso oggi (forza con <em>"Ri-emetti"</em>)</li>
                        </ul>
                        <div className="mt-2 bg-amber-900/20 border border-amber-700/30 rounded-md p-3 text-amber-200 text-xs">
                            <AlertCircle className="w-4 h-4 inline mr-1" /> La trasmissione SdI/AdE è <strong>simulata</strong>: in produzione richiede driver fiscale hardware certificato.
                        </div>
                    </>
                ),
            },
        ],
    },

    {
        id: "ui-customization",
        title: "Personalizzazione interfaccia",
        icon: Smartphone,
        roles: ["admin", "cashier", "waiter", "kitchen"],
        items: [
            {
                id: "layout-mode",
                title: "Modalità Layout (Smartphone / Tablet / Desktop)",
                body: (
                    <p className="text-sm">
                        Click sull'<strong>avatar in alto a destra</strong> → scegli tra Smartphone (max 480px, ideale per camerieri sul cellulare), Tablet (max 1024px, ottimo per terminali fissi), Desktop (schermo intero per cassa). La scelta è salvata localmente per device.
                    </p>
                ),
            },
            {
                id: "logout",
                title: "Disconnessione",
                body: (
                    <p className="text-sm">
                        Stesso menu avatar → <strong>"Esci"</strong>. La sessione JWT scade automaticamente dopo 12h.
                    </p>
                ),
            },
        ],
    },

    {
        id: "billing",
        title: "Abbonamento & quota AI",
        icon: CreditCard,
        roles: ["admin"],
        items: [
            {
                id: "subscription",
                title: "Gestire l'abbonamento",
                body: (
                    <>
                        <p className="text-sm mb-2">
                            <strong>Sidebar → Abbonamento</strong>:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>14 giorni di trial Premium gratuiti all'attivazione</li>
                            <li>Sottoscrivi via Stripe (carta) per piano Premium mensile/annuale</li>
                            <li>Acquista <strong>pacchetti AI</strong> (50 richieste a €10) per generare più Insights e import menu</li>
                        </ul>
                        <p className="text-sm mt-2">
                            Il contatore quota AI è visibile nell'header in alto a destra (icona <Sparkles className="w-3.5 h-3.5 inline" />).
                        </p>
                    </>
                ),
            },
        ],
    },

    {
        id: "troubleshooting",
        title: "Risoluzione problemi comuni",
        icon: AlertCircle,
        roles: ["admin", "cashier", "waiter", "kitchen"],
        items: [
            {
                id: "trouble-not-logged",
                title: "Non riesco ad accedere",
                body: (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Verifica email/password con il tuo admin</li>
                        <li>Se l'account è disattivato, l'admin deve riattivarlo da <strong>Utenti</strong></li>
                        <li>Per il QR: chiedi all'admin di rigenerarlo (icona ↻ su Terminali Camerieri)</li>
                        <li>Cancella la cache del browser e riprova</li>
                    </ul>
                ),
            },
            {
                id: "trouble-kitchen-not-receiving",
                title: "La cucina non riceve le comande",
                body: (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li>Verifica che la connessione WebSocket sia attiva (badge "Sync" verde nell'header)</li>
                        <li>Controlla che il piatto abbia il <em>reparto</em> giusto (cucina/pizzeria/bar)</li>
                        <li>Sulla pagina Cucina, ricarica con <strong>F5</strong> per re-sincronizzare</li>
                    </ul>
                ),
            },
            {
                id: "trouble-stripe-failed",
                title: "Pagamento abbonamento fallito",
                body: (
                    <p className="text-sm">
                        Verifica che la carta sia valida e abbia saldo. Per problemi persistenti contatta il supporto Stripe via la pagina Abbonamento. Tutti i pagamenti sono gestiti su <strong>checkout.stripe.com</strong>, Smart Table non memorizza dati di carta.
                    </p>
                ),
            },
            {
                id: "trouble-prenova-401",
                title: "Errore 401 / 403 su renova360",
                body: (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        <li><strong>401</strong>: API Key errata o scaduta — rigenerala in Prenova</li>
                        <li><strong>403</strong>: ristorante disattivato — verifica lo stato in Prenova</li>
                        <li><strong>5xx</strong>: retry automatico esponenziale (2s, 8s, 32s)</li>
                    </ul>
                ),
            },
        ],
    },
];

const Guida = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [openSection, setOpenSection] = useState("quickstart");

    // Filtra sezioni in base al ruolo
    const visibleSections = useMemo(() => {
        return SECTIONS.filter((s) => s.roles.includes(user.role));
    }, [user.role]);

    // Ricerca testuale
    const filteredSections = useMemo(() => {
        if (!search.trim()) return visibleSections;
        const q = search.toLowerCase();
        return visibleSections
            .map((s) => ({
                ...s,
                items: s.items.filter((it) => {
                    const txt = (it.title + " " + (typeof it.body === "string" ? it.body : "")).toLowerCase();
                    return txt.includes(q) || s.title.toLowerCase().includes(q);
                }),
            }))
            .filter((s) => s.items.length > 0 || s.title.toLowerCase().includes(q));
    }, [visibleSections, search]);

    return (
        <div className="space-y-6" data-testid="guida-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Documentazione</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <BookOpen className="w-9 h-9 text-brand" /> Guida d'uso
                    </h1>
                    <p className="text-elegant mt-2 max-w-2xl">
                        Istruzioni dettagliate per ogni flusso operativo. Le sezioni sono filtrate in base al tuo ruolo (<strong className="text-brand">{user.role}</strong>).
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={async () => {
                            try {
                                toast("Generazione PowerPoint in corso...", { duration: 2000 });
                                const res = await api.get("/training/pptx", { responseType: "blob" });
                                // Estrae il filename dall'header Content-Disposition (tenant-customized)
                                let filename = "Formazione_SmartTable.pptx";
                                const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"] || "";
                                const m1 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
                                const m2 = /filename="?([^";]+)"?/i.exec(cd);
                                if (m1) {
                                    try { filename = decodeURIComponent(m1[1]); } catch { /* keep default */ }
                                } else if (m2) {
                                    filename = m2[1];
                                }
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = filename;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                setTimeout(() => URL.revokeObjectURL(url), 30_000);
                                toast.success("PowerPoint scaricato!");
                            } catch (e) {
                                toast.error(formatError(e));
                            }
                        }}
                        data-testid="download-training-pptx"
                        className="h-12 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-sm uppercase tracking-wider flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" /> Scarica PPT Formazione
                    </button>
                    <button onClick={() => navigate("/dashboard")} className="h-12 px-5 border border-[#1f2c44] hover:border-brand rounded-md text-sm">
                        ← Torna alla Dashboard
                    </button>
                </div>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 flex items-center gap-3">
                <Search className="w-4 h-4 text-elegant" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cerca nella guida... (es. 'pagamento', 'qr', 'cucina')"
                    data-testid="guida-search"
                    className="flex-1 bg-transparent text-warm focus:outline-none text-sm"
                />
                {search && (
                    <button onClick={() => setSearch("")} className="text-elegant hover:text-brand text-xs">
                        Pulisci
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar TOC */}
                <aside className="hidden lg:block lg:col-span-1">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-3 sticky top-24">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant px-2 py-1">Indice</div>
                        {visibleSections.map((s) => {
                            const Icon = s.icon;
                            const active = openSection === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => {
                                        setOpenSection(s.id);
                                        document.getElementById(`guide-section-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    data-testid={`toc-${s.id}`}
                                    className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors ${
                                        active ? "bg-brand/15 text-brand" : "text-elegant hover:text-warm hover:bg-[#0e1626]"
                                    }`}
                                >
                                    <Icon className="w-4 h-4 flex-shrink-0" />
                                    <span className="flex-1 truncate">{s.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* Sezioni */}
                <div className="lg:col-span-3 space-y-4">
                    {filteredSections.length === 0 && (
                        <div className="bg-surface border border-[#1f2c44] rounded-lg p-8 text-center text-elegant">
                            Nessun risultato per "<strong className="text-warm">{search}</strong>". Prova con un'altra parola chiave.
                        </div>
                    )}
                    {filteredSections.map((s) => {
                        const Icon = s.icon;
                        const isOpen = openSection === s.id || search.trim().length > 0;
                        return (
                            <section key={s.id} id={`guide-section-${s.id}`}
                                className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden"
                                data-testid={`section-${s.id}`}>
                                <button
                                    onClick={() => setOpenSection(isOpen && openSection === s.id ? null : s.id)}
                                    className="w-full px-5 py-4 flex items-center gap-3 hover:bg-[#0e1626] transition-colors"
                                >
                                    <Icon className="w-5 h-5 text-brand flex-shrink-0" />
                                    <div className="flex-1 text-left">
                                        <div className="font-display font-bold text-lg">{s.title}</div>
                                        <div className="text-[11px] text-elegant">{s.items.length} argomenti</div>
                                    </div>
                                    {isOpen ? <ChevronDown className="w-4 h-4 text-elegant" /> : <ChevronRight className="w-4 h-4 text-elegant" />}
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 space-y-4 border-t border-[#1f2c44] pt-4">
                                        {s.items.map((it) => (
                                            <div key={it.id} className="bg-deep border border-[#1f2c44] rounded-md p-4" data-testid={`item-${it.id}`}>
                                                <h3 className="font-display font-bold text-warm mb-2 flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-brand" />
                                                    {it.title}
                                                </h3>
                                                <div className="text-elegant leading-relaxed text-sm">{it.body}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}

                    <div className="bg-brand/5 border border-brand/30 rounded-lg p-5 text-center">
                        <Lightbulb className="w-8 h-8 text-brand mx-auto mb-2" />
                        <div className="font-display font-bold text-warm mb-1">Altre domande?</div>
                        <div className="text-elegant text-sm">
                            Smart Table 360 by renova360 · MAKAI di Maria Laura Pellino · P.IVA 09936410969
                            <br />
                            Per supporto tecnico, contatta il tuo amministratore di sistema.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Guida;
